import React, { useState } from 'react';
import { motion } from 'motion/react';
import { MapPin, Mail, Phone, Send, Clock, Globe, ExternalLink, AtSign } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { Section, PageHero } from '../components/layout/Section';
import { SpotlightCard, WindowFrame, SpecRow, StatusPill } from '../components/layout/Tech';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const fadeInUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

const mapLocation = encodeURIComponent(`${companyInfo.fullName}, ${companyInfo.location}`);
const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapLocation}`;
const openStreetMapEmbedUrl = 'https://www.openstreetmap.org/export/embed.html?bbox=75.450%2C30.250%2C78.950%2C32.650&layer=mapnik&marker=31.1048%2C77.1734';


export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', inquiry: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const buildEmail = () => {
    const inquiry = formData.inquiry || 'general';
    const subject = `Globistaan contact: ${inquiry} inquiry from ${formData.name}`;
    const body = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Company: ${formData.company || 'Not provided'}`,
      `Inquiry Type: ${inquiry}`,
      '',
      'Message:',
      formData.message,
      '',
      `Submitted At: ${new Date().toLocaleString()}`
    ].join('\n');

    return { subject, body };
  };

  const openMailFallback = ({ subject, body }) => {
    const mailto = `mailto:${companyInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const { subject, body } = buildEmail();
    const payload = new FormData();
    payload.append('_subject', subject);
    payload.append('_template', 'box');
    payload.append('_captcha', 'false');
    payload.append('_replyto', formData.email);
    payload.append('Name', formData.name);
    payload.append('Email', formData.email);
    payload.append('Company', formData.company || 'Not provided');
    payload.append('Inquiry Type', formData.inquiry || 'General');
    payload.append('Message', formData.message);
    payload.append('Submitted At', new Date().toISOString());
    payload.append('Email Body', body);

    setSending(true);
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${companyInfo.email}`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: payload
      });

      if (!response.ok) {
        throw new Error('Email service unavailable');
      }

      setSubmitted(true);
      toast.success('Your message has been emailed to Globistaan.');
    } catch (error) {
      openMailFallback({ subject, body });
      toast.info('Opening your email app with the message ready to send.');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactItems = [
    { icon: MapPin, label: 'Location', value: companyInfo.location, accent: '#22D3EE' },
    { icon: Mail, label: 'Email', value: companyInfo.email, accent: '#A78BFA' },
    { icon: Phone, label: 'Phone', value: companyInfo.phone, accent: '#34D399' },
    { icon: Globe, label: 'Website', value: companyInfo.website, accent: '#60A5FA' },
    { icon: AtSign, label: 'X', value: companyInfo.xHandle, accent: '#FB7185' },
    { icon: Clock, label: 'Business Hours', value: 'Mon - Fri, 9:00 AM - 6:00 PM IST', accent: '#FBBF24' }
  ];

  return (
    <main>
      {/* Hero */}
      <PageHero
        tone="teal"
        eyebrow="Contact Us"
        title="Let's Start a Conversation"
        subtitle="Have a project in mind? Looking to invest? Or just want to say hello?"
        minHeight="50vh"
        glows={[
          { color: 'rgba(52,211,153,0.26)', top: '-10%', left: '-6%', size: 560 },
          { color: 'rgba(34,211,238,0.22)', bottom: '-20%', right: '-6%', size: 560 }
        ]}
        ridge
      />

      {/* Form + Info */}
      <Section
        tone="navy"
        pattern="grid"
        aurora
        topo
        glows={[
          { color: 'rgba(34,211,238,0.2)', top: '-10%', right: '-6%', size: 600 },
          { color: 'rgba(124,58,237,0.24)', bottom: '-10%', left: '-6%', size: 600 }
        ]}
      >
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div {...fadeInUp} className="lg:col-span-2">
            <h2 className="h-section mb-6" style={{ fontSize: '1.6rem' }}>Get in Touch</h2>
            <SpotlightCard accent="accent-cyan" style={{ '--card-pad': '0.5rem 1.5rem' }}>
              {contactItems.map((item) => (
                <SpecRow key={item.label} label={item.label} icon={item.icon} accent={item.accent}>{item.value}</SpecRow>
              ))}
            </SpotlightCard>

            {/* Open map preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-8"
            >
              <WindowFrame url="openstreetmap.org · Baijnath, Kangra" right={<StatusPill status="Live" />}>
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open Globistaan location in Google Maps"
                className="relative block no-underline group"
                style={{ height: '210px', background: '#e8ecef' }}
              >
                <iframe
                  title="Globistaan location map"
                  src={openStreetMapEmbedUrl}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  style={{ pointerEvents: 'none', filter: 'saturate(0.92) contrast(1.02)' }}
                />
                <span
                  className="absolute left-4 right-4 bottom-4 flex items-center justify-between gap-3 rounded-xl px-4 py-3 transition-transform duration-200 group-hover:-translate-y-0.5"
                  style={{
                    background: 'rgba(7,10,18,0.84)',
                    color: '#ffffff',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(34,211,238,0.25)'
                  }}
                >
                  <span className="flex items-center gap-2 text-sm font-medium min-w-0">
                    <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: '#22D3EE' }} />
                    <span className="truncate">{companyInfo.location}</span>
                  </span>
                  <span className="flex items-center gap-1 text-xs flex-shrink-0" style={{ fontFamily: 'var(--font-mono)', color: 'rgba(255,255,255,0.72)' }}>
                    Open Map <ExternalLink className="w-3.5 h-3.5" />
                  </span>
                </span>
              </a>
              </WindowFrame>
            </motion.div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-3"
          >
            {submitted ? (
              <SpotlightCard accent="accent-green" style={{ '--card-pad': '3rem', textAlign: 'center' }}>
                <div className="icon-well lg mx-auto mb-6 float-animation" style={{ '--card-accent': '#34D399' }}>
                  <Send className="w-6 h-6" />
                </div>
                <h3 className="mb-4" style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)' }}>Message Sent!</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.65, color: 'var(--text-secondary)', maxWidth: '400px', margin: '0 auto' }}>
                  Thank you for reaching out. We'll get back to you within 24-48 business hours.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', company: '', inquiry: '', message: '' }); }}
                  className="btn-secondary mt-8"
                >
                  Send Another Message
                </button>
              </SpotlightCard>
            ) : (
              <SpotlightCard accent="accent-cyan" style={{ '--card-pad': 'clamp(1.5rem, 4vw, 2.75rem)' }}>
                <div className="flex items-center justify-between gap-4 mb-8">
                  <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)' }}>Send Us a Message</h3>
                  <span className="chip">reply within 24-48h</span>
                </div>
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="field-label">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Your name"
                        className="field"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="field-label">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="your@email.com"
                        className="field"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company" className="field-label">Company</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        placeholder="Your company"
                        className="field"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <Label className="field-label">Inquiry Type</Label>
                      <Select onValueChange={(val) => handleChange('inquiry', val)}>
                        <SelectTrigger className="field" data-testid="contact-inquiry-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="project">New Project</SelectItem>
                          <SelectItem value="investment">Investment</SelectItem>
                          <SelectItem value="partnership">Partnership</SelectItem>
                          <SelectItem value="careers">Careers</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message" className="field-label">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="Tell us about your project or inquiry..."
                      rows={5}
                      className="field"
                      style={{ resize: 'vertical' }}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="btn-primary btn-accent self-start"
                    style={{ padding: '0.9rem 2rem', opacity: sending ? 0.7 : 1, cursor: sending ? 'not-allowed' : 'pointer' }}
                    data-testid="contact-submit-btn"
                  >
                    {sending ? 'Sending...' : 'Send Message'} <Send className="w-4 h-4" />
                  </button>
                </form>
              </SpotlightCard>
            )}
          </motion.div>
        </div>
      </Section>
    </main>
  );
}
